import { createContext, useContext, useEffect, useState } from "react";
import type { PresenceDTO, UserDTO } from "../shared/types";
import { Icon, type IconName } from "./components/Icon";
import { IconButton } from "./components/IconButton";
import { Drawer } from "./components/Dialog";
import { Avatar, Button } from "./components/ui";
import { UpdateToast } from "./components/UpdateToast";
import { get, post } from "./lib/api";
import { Link, match, navigate, useLocation } from "./lib/router";
import { realtime, type ConnectionState } from "./lib/ws";
import { ChatPage } from "./pages/Chat";
import { LoginPage } from "./pages/Login";
import { AdminLive } from "./pages/admin/Live";
import { AdminRunDetail } from "./pages/admin/RunDetail";
import { AdminAgents } from "./pages/admin/Agents";
import { AdminAgentDetail } from "./pages/admin/AgentDetail";
import { AdminConnectors } from "./pages/admin/Connectors";
import { AdminApiClients } from "./pages/admin/ApiClients";
import { AdminSkills } from "./pages/admin/Skills";
import { AdminScopes } from "./pages/admin/Scopes";
import { AdminScopeType } from "./pages/admin/ScopeType";
import { AdminScope } from "./pages/admin/Scope";
import { AdminFiles } from "./pages/admin/Files";
import { AdminProxyLog } from "./pages/admin/ProxyLog";
import { AdminAudit } from "./pages/admin/Audit";
import { AdminConversations } from "./pages/admin/Conversations";
import { AdminUsers } from "./pages/admin/Users";
import { AdminModels } from "./pages/admin/Models";
import { AdminSlack } from "./pages/admin/Slack";
import { AdminChannels } from "./pages/admin/Channels";
import { AdminPages } from "./pages/admin/Pages";
import { PlatformAssistant } from "./components/PlatformAssistant";
import { AdminMeetings } from "./pages/admin/Meetings";
import { AdminEmail } from "./pages/admin/Email";
import { PublicMeetingPage } from "./pages/PublicMeeting";
import { PageViewPage } from "./pages/PageView";
import { ConnectionDonePage, ConnectionsPage } from "./pages/Connections";
import { FilesPage } from "./pages/Files";
import { MeetingPage, MeetingsPage } from "./pages/Meetings";
import { ApprovalsPage, RequestPage } from "./pages/Approvals";
import { ReviewTemplatePage } from "./pages/Review";
import { AboutMePage } from "./pages/AboutMe";
import { WishlistPage } from "./pages/Wishlist";
import { AdminMemories } from "./pages/admin/Memories";
import { AdminNotes } from "./pages/admin/Notes";
import { AdminVoiceLab } from "./pages/admin/VoiceLab";

const UserContext = createContext<UserDTO | null>(null);
export const useUser = () => useContext(UserContext)!;

/** Pages render their own secondary navigation (e.g. the chat list) into this drawer on mobile. */
export const MobileNavContext = createContext<{ open: boolean; setOpen: (v: boolean) => void }>({ open: false, setOpen: () => {} });

const ADMIN_NAV: { group: string; items: [to: string, label: string, icon: IconName][] }[] = [
  { group: "Monitor", items: [["/admin", "Live", "live"], ["/admin/conversations", "Conversations", "conversations"], ["/admin/approvals", "Requests", "audit"], ["/admin/wishlist", "Wishlist", "star"], ["/admin/meetings", "Meetings", "meetings"]] },
  { group: "Build", items: [["/admin/agents", "Agents", "agents"], ["/admin/scopes", "Scopes", "scopes"], ["/admin/skills", "Skills", "skills"], ["/admin/pages", "Pages", "pages"], ["/admin/connectors", "Connectors", "connectors"]] },
  { group: "Memory", items: [["/admin/memories", "Memories", "memory"], ["/admin/notes", "User notes", "users"], ["/admin/files", "Files", "files"]] },
  { group: "Governance", items: [["/admin/api-clients", "API clients", "key"], ["/admin/proxy", "Proxy log", "proxy"], ["/admin/audit", "Audit log", "audit"]] },
  { group: "Settings", items: [["/admin/users", "Users", "users"], ["/admin/channels", "Channels", "chat"], ["/admin/models", "Models", "workflows"]] },
];

function ConnectionDot() {
  const [state, setState] = useState<ConnectionState>(realtime.state);
  useEffect(() => realtime.onState(setState), []);
  const color = state === "open" ? "bg-success" : state === "connecting" ? "bg-warning" : "bg-faint";
  return <span title={`realtime: ${state}`} className={`inline-block size-2 shrink-0 rounded-full ring-2 ring-surface ${color}`} />;
}

function HealthBanner() {
  const [problems, setProblems] = useState<string[]>([]);
  useEffect(() => {
    const load = () => get("/api/health").then((h) => setProblems(h.problems ?? []), () => {});
    load();
    // A replica going away is a problem that starts and ends while the page is open.
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, []);
  if (!problems.length) return null;
  return (
    <div className="bg-warning/10 px-4 py-1.5 text-center text-xs text-warning">
      {problems.map((p, i) => (
        <span key={p}>
          {i > 0 && " · "}
          {p}
        </span>
      ))}
    </div>
  );
}

/** In the phone drawer, an admin's own pages the top bar has no room for below sm. */
const ADMIN_DRAWER_OWN: (typeof ADMIN_NAV)[number] = { group: "You", items: [["/files", "Your files", "files"]] };

/** What `GET /api/approvals/count` says: what waits for this person to decide (D14), and how much of it is stale. */
type RequestCount = { pending: number; mine: number; stale: number; owner: boolean };

/** The count on Requests: how many wait for you, and in its tooltip how many have waited over three days. */
function CountBubble({ count }: { count: RequestCount }) {
  if (count.mine <= 0) return null;
  const title = `${count.mine} waiting for you${count.stale ? `, ${count.stale} for over three days` : ""}`;
  return (
    <span title={title} className="ml-auto grid min-w-5 place-items-center rounded-full bg-warning px-1 text-tiny font-semibold text-white">
      {count.mine}
    </span>
  );
}

function AdminNavLinks({ pathname, onNavigate, count, own = false }: { pathname: string; onNavigate?: () => void; count: RequestCount; own?: boolean }) {
  return (
    <div className="space-y-5">
      {(own ? [ADMIN_DRAWER_OWN, ...ADMIN_NAV] : ADMIN_NAV).map(({ group, items }) => (
        <div key={group}>
          <div className="mb-1 px-3 text-tiny font-medium tracking-wide text-faint uppercase">{group}</div>
          <div className="space-y-0.5">
            {items.map(([to, label, icon]) => {
              const active = to === "/admin" ? pathname === "/admin" || pathname.startsWith("/admin/runs") : pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={onNavigate}
                  className={`flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-colors md:py-1.5 ${
                    active
                      ? "bg-surface font-medium text-fg shadow-sm ring-1 ring-line"
                      : "text-muted hover:bg-fill hover:text-fg"
                  }`}
                >
                  <span className={active ? "text-ink dark:text-haze" : "text-faint"}>
                    <Icon name={icon} />
                  </span>
                  {label}
                  {to === "/admin/approvals" && <CountBubble count={count} />}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Shell({ user, children }: { user: UserDTO; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const inAdmin = pathname.startsWith("/admin");
  const inPages = pathname.startsWith("/admin/pages");
  // A template's own page is opened from Requests and closes back to it.
  const inApprovals = pathname.startsWith("/approvals") || pathname.startsWith("/review/");
  const inConnections = pathname === "/connections" || pathname === "/connections/done";
  const inFiles = pathname === "/files";
  const inMeetings = pathname === "/meetings" || pathname.startsWith("/meetings/");
  const inWishlist = pathname === "/wishlist";
  const [approvals, setApprovals] = useState<RequestCount>({ pending: 0, mine: 0, stale: 0, owner: false });
  // Colleagues with the app open right now.
  const [live, setLive] = useState<PresenceDTO[]>([]);
  useEffect(() => {
    // Being here is what makes you live, so hold a socket open even with nothing subscribed.
    realtime.ensureConnected();
    const load = () => get<PresenceDTO[]>("/api/presence").then(setLive, () => {});
    load();
    // On a fresh page load the socket registers just after this mounts, so ask again once it is open.
    const offState = realtime.onState((state) => state === "open" && load());
    const timer = setInterval(load, 20_000);
    return () => {
      offState();
      clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    // Only what this person can decide: an admin's count is theirs, never everyone's (D14).
    get<RequestCount>("/api/approvals/count").then(setApprovals, () => {});
  }, [pathname]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pageNavOpen, setPageNavOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    setPageNavOpen(false);
  }, [pathname]);

  const signOut = async () => {
    const res = await post<{ auth0LogoutUrl?: string | null }>("/api/logout").catch(() => ({}) as { auth0LogoutUrl?: string | null });
    if (res.auth0LogoutUrl) {
      location.href = res.auth0LogoutUrl;
      return;
    }
    navigate("/login");
    location.reload();
  };

  // Tighter at phone width: the bar holds up to five tabs and has to fit 390 px with no sideways scroll.
  const tab = (active: boolean) =>
    `rounded-full px-2 py-1 transition-all sm:px-3 ${
      active
        ? "bg-raised font-medium text-fg shadow-sm"
        : "text-muted hover:text-fg"
    }`;

  return (
    <MobileNavContext.Provider value={{ open: pageNavOpen, setOpen: setPageNavOpen }}>
      <div className="flex h-dvh flex-col">
        <HealthBanner />
        <header className="glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-line px-2 md:gap-4 md:px-4">
          {user.role === "admin" && (
            <IconButton
              label="Open menu"
              fill="none"
              className="md:hidden"
              onClick={() => (inAdmin ? setMenuOpen(true) : setPageNavOpen(true))}
            >
              <Icon name="menu" />
            </IconButton>
          )}
          {/* Below sm the menu and the Chat tab lead home, and the bar needs the room for Connections. */}
          <Link to="/" className="hidden shrink-0 items-center gap-2.5 font-semibold tracking-tight sm:flex">
            <img src="/favicon-32.png" alt="" className="size-7 shrink-0 rounded-lg shadow-sm ring-1 ring-line" />
            <span className="hidden sm:inline">AgentOS</span>
          </Link>
          <nav className="ml-1 flex gap-0.5 rounded-full bg-fill p-1 text-sm md:ml-4">
            <Link to="/" className={tab(!inAdmin && !inConnections && !inFiles && !inMeetings && !inWishlist)}>
              Chat
            </Link>
            {/* A member only hears about requests while one is waiting for them. */}
            {user.role !== "admin" && (approvals.mine > 0 || inApprovals) && (
              <Link to="/approvals" className={`inline-flex items-center gap-1.5 ${tab(inApprovals)}`}>
                Requests
                <CountBubble count={approvals} />
              </Link>
            )}
            {/* Below sm Files and Connections are their icons, and an admin's files are in the menu. */}
            <Link to="/files" aria-label="Files" title="Files" className={`${user.role === "admin" ? "hidden sm:block" : ""} ${tab(inFiles)}`}>
              <Icon name="files" className="size-4 sm:hidden" />
              <span className="hidden sm:inline">Files</span>
            </Link>
            {/* Everyone's own meetings, private to the people in them (AIT-335); an icon below sm, like Files. */}
            <Link to="/meetings" aria-label="Meetings" title="Meetings" className={tab(inMeetings)}>
              <Icon name="meetings" className="size-4 sm:hidden" />
              <span className="hidden sm:inline">Meetings</span>
            </Link>
            {/* An admin's is under Monitor, beside Requests. */}
            {user.role !== "admin" && (
              <Link to="/wishlist" className={tab(inWishlist)}>
                Wishlist
              </Link>
            )}
            <Link to="/connections" aria-label="Connections" title="Connections" className={tab(inConnections)}>
              <Icon name="connectors" className="size-4 sm:hidden" />
              <span className="hidden sm:inline">Connections</span>
            </Link>
            {user.role === "admin" && (
              <>
                <Link to="/admin/pages" className={`hidden sm:block ${tab(inPages)}`}>
                  Pages
                </Link>
                <Link to="/admin" className={tab(inAdmin && !inPages)}>
                  Admin
                </Link>
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm md:gap-3">
            {live.length > 0 && (
              <span
                className="hidden items-center gap-1.5 rounded-full bg-fill py-1 pr-2.5 pl-1 md:inline-flex"
                title={`Live now: ${live.map((p) => p.name).join(", ")}`}
              >
                <span className="flex -space-x-1.5">
                  {live.slice(0, 5).map((p) => (
                    <span key={p.id} className="rounded-full ring-2 ring-canvas" title={p.name}>
                      <Avatar name={p.name} size="sm" />
                    </span>
                  ))}
                </span>
                <span className="text-xs font-medium text-muted">
                  {live.length > 5 ? `+${live.length - 5} · ` : ""}live
                </span>
              </span>
            )}
            <span className="relative hidden md:inline-flex">
              <Avatar name={user.name} size="sm" />
              <span className="absolute -right-0.5 -bottom-0.5">
                <ConnectionDot />
              </span>
            </span>
            <span className="md:hidden">
              <ConnectionDot />
            </span>
            <Link to="/me" className="hidden rounded-full px-2 py-1 font-medium text-muted transition hover:bg-fill hover:text-fg md:inline" title="About me">
              {user.name}
            </Link>
            {/* An icon alone at phone width, so the bar fits in 390 px; the Button's own display wins over `hidden`. */}
            <Button variant="ghost" className="items-center gap-1.5" onClick={signOut} title="Sign out" aria-label="Sign out">
              <Icon name="signout" className="size-4" /> <span className="hidden md:inline">Sign out</span>
            </Button>
          </div>
        </header>

        <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="flex items-center gap-2 font-semibold tracking-tight">
              <img src="/favicon-32.png" alt="" className="size-6 rounded-md" /> Admin
            </span>
            <IconButton label="Close menu" onClick={() => setMenuOpen(false)}>
              <Icon name="close" className="size-4" />
            </IconButton>
          </div>
          <nav className="flex-1 overflow-auto px-2 pb-4">
            <AdminNavLinks pathname={pathname} onNavigate={() => setMenuOpen(false)} count={approvals} own />
          </nav>
          <div className="flex items-center gap-2 border-t border-line p-3 text-sm">
            <Avatar name={user.name} size="sm" />
            <Link to="/me" className="min-w-0 flex-1 truncate font-medium">
              {user.name}
            </Link>
            <Button onClick={signOut}>Sign out</Button>
          </div>
        </Drawer>

        {inAdmin ? (
          <div className="flex min-h-0 flex-1">
            <aside className="scroll-thin hidden w-60 shrink-0 overflow-auto px-3 py-5 md:block">
              <AdminNavLinks pathname={pathname} count={approvals} />
            </aside>
            <main className="scroll-thin min-w-0 flex-1 overflow-x-hidden overflow-y-auto">{children}</main>
            {user.role === "admin" && <PlatformAssistant />}
          </div>
        ) : (
          <main className="min-h-0 flex-1">{children}</main>
        )}
      </div>
      <UpdateToast />
    </MobileNavContext.Provider>
  );
}

/** An address that moved: replaced in the history, so Back skips it. */
function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, true), [to]);
  return null;
}

function Routes() {
  const { pathname } = useLocation();
  const user = useUser();
  let m: Record<string, string> | null;

  // One page across every chat: choosing an agent swaps the conversation, keeping the sidebar and the shell as they
  // are. The page empties its own draft and attachments when the chat changes, so nothing follows you across.
  if ((m = match("/c/:id", pathname))) return <ChatPage conversationId={m.id} />;
  if ((m = match("/a/:slug", pathname))) return <ChatPage agentSlug={m.slug} />;
  if (pathname === "/") return <ChatPage />;
  if (pathname === "/me") return <AboutMePage />;
  if (pathname === "/connections") return <ConnectionsPage />;
  if (pathname === "/connections/done") return <ConnectionDonePage />;
  if (pathname === "/files") return <FilesPage />;
  if (pathname === "/meetings") return <MeetingsPage />;
  if ((m = match("/meetings/:id", pathname))) return <MeetingPage meetingId={m.id!} />;
  // An admin's is under Monitor, so a link Pandito sent opens it there.
  if (pathname === "/wishlist") return user.role === "admin" ? <Redirect to={`/admin/wishlist${location.search}`} /> : <WishlistPage />;
  if (pathname === "/approvals") return <ApprovalsPage />;
  if ((m = match("/approvals/:id", pathname))) return <RequestPage approvalId={m.id!} />;
  // A template's own page is one view over the requests, open to everyone signed in, drawn over Requests. It has an
  // admin address too, so one opened from Admin → Requests keeps the admin shell and goes back there.
  if ((m = match("/review/:template", pathname)))
    return (
      <>
        <ApprovalsPage />
        <ReviewTemplatePage template={m.template!} />
      </>
    );
  if (pathname.startsWith("/admin") && user.role !== "admin") return <div className="p-8">Admins only.</div>;
  if ((m = match("/admin/review/:template", pathname)))
    return (
      <>
        <ApprovalsPage admin />
        <ReviewTemplatePage template={m.template!} admin />
      </>
    );
  if (pathname === "/admin") return <AdminLive />;
  if (pathname === "/admin/approvals") return <ApprovalsPage admin />;
  if ((m = match("/admin/approvals/:id", pathname))) return <RequestPage approvalId={m.id!} admin />;
  if ((m = match("/admin/runs/:id", pathname))) return <AdminRunDetail runId={m.id} />;
  if (pathname === "/admin/agents") return <AdminAgents />;
  if ((m = match("/admin/agents/:id", pathname))) return <AdminAgentDetail agentId={m.id!} />;
  if (pathname === "/admin/skills") return <AdminSkills />;
  if ((m = match("/admin/skills/:id", pathname))) return <AdminSkills skillId={m.id} />;
  if (pathname === "/admin/connectors") return <AdminConnectors />;
  if (pathname === "/admin/api-clients") return <AdminApiClients />;
  if (pathname === "/admin/scopes") return <AdminScopes />;
  if ((m = match("/admin/scopes/:type", pathname))) return <AdminScopeType typeKey={m.type!} />;
  if ((m = match("/admin/scopes/:type/:id", pathname))) return <AdminScope scopeId={m.id!} />;
  // Subjects became scopes: old links land on the new pages.
  if (pathname === "/admin/subjects") return <Redirect to="/admin/scopes" />;
  if ((m = match("/admin/subjects/:id", pathname))) return <AdminScope scopeId={m.id!} />;
  if (pathname === "/admin/files") return <AdminFiles />;
  if (pathname === "/admin/pages") return <AdminPages />;
  if (pathname === "/admin/meetings") return <AdminMeetings />;
  if ((m = match("/admin/pages/:id", pathname))) return <AdminPages pageId={m.id} />;
  if (pathname === "/admin/conversations") return <AdminConversations />;
  if ((m = match("/admin/conversations/:id", pathname))) return <AdminConversations conversationId={m.id} />;
  // The toolbox's list moved to the Wishlist (AIT-106): old links land on its tools.
  if (pathname === "/admin/toolbox") return <Redirect to="/admin/wishlist?type=tool" />;
  if (pathname === "/admin/wishlist") return <WishlistPage />;
  if (pathname === "/admin/voice-lab") return <AdminVoiceLab />;
  if (pathname === "/admin/proxy") return <AdminProxyLog />;
  if (pathname === "/admin/audit") return <AdminAudit />;
  if (pathname === "/admin/memories") return <AdminMemories />;
  if (pathname === "/admin/notes") return <AdminNotes />;
  if (pathname === "/admin/users") return <AdminUsers />;
  if (pathname === "/admin/models") return <AdminModels />;
  if (pathname === "/admin/channels") return <AdminChannels />;
  if (pathname === "/admin/channels/slack") return <AdminSlack />;
  if (pathname === "/admin/channels/email") return <AdminEmail />;
  // Slack and Email lived on their own pages before Channels; old links land on the new ones.
  if (pathname === "/admin/slack") return <Redirect to="/admin/channels/slack" />;
  if (pathname === "/admin/email") return <Redirect to="/admin/channels/email" />;
  return <div className="p-8">Not found.</div>;
}

export function App() {
  const { pathname } = useLocation();
  const [user, setUser] = useState<UserDTO | null | undefined>(undefined);
  // Public meeting pages need no account (and must not redirect to sign-in).
  const publicMeeting = match("/m/:token", pathname);
  // A hosted page draws its own frame and asks to sign in only when it is not public; any path under it is the page.
  const hostedPage = /^\/p\/([0-9a-f-]{36})(?:\/|$)/i.exec(pathname)?.[1];

  useEffect(() => {
    if (pathname === "/login" || publicMeeting || hostedPage) return;
    get<{ user: UserDTO }>("/api/me").then(
      (r) => setUser(r.user),
      () => setUser(null),
    );
  }, [pathname === "/login", Boolean(publicMeeting), Boolean(hostedPage)]);

  if (publicMeeting) return <PublicMeetingPage token={publicMeeting.token!} />;
  if (hostedPage) return <PageViewPage pageId={hostedPage.toLowerCase()} />;
  if (pathname === "/login") return <LoginPage onLogin={(u) => setUser(u)} />;
  if (user === undefined) return <div className="grid h-dvh place-items-center text-sm text-faint">Loading…</div>;
  if (user === null) return null;

  return (
    <UserContext.Provider value={user}>
      <Shell user={user}>
        <Routes />
      </Shell>
    </UserContext.Provider>
  );
}
